import { useCallback, useRef, useEffect } from 'react';

import { Delete } from '@mui/icons-material';
import { IconButton, useTheme } from '@mui/material';
import { TableColumn } from 'components/Table';
import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { RefetchOptions } from 'axios-hooks';
import { FieldValues, UseControllerProps } from 'react-hook-form';
import { useParams, useMatch } from 'react-router-dom';

import { url } from 'constants/index';
import { useTableFilter } from 'hooks/common';

import { BaseCompetitorsFormField } from './BaseCompetitorsFormField';
import { getCompetitorStateColors } from './CompetitorsFormField.styles';

interface CompetitorsFormFieldProps {
  refetchCompetitors: (config?: AxiosRequestConfig, options?: RefetchOptions) => AxiosPromise<any>;
  competitors: Competitor[];
  genderType?: string;
  onEditCell?;
  sportId?: number;
  seasonId?: number;
  required?: boolean;
  customColumns?: Array<TableColumn>;
  customFilters?: Array<PaginationFilter>;
}

interface WithCompetitorsValues extends FieldValues {
  competitors: Array<EditableCompetitor>;
}

export const CompetitorsFormField = <T extends WithCompetitorsValues>({
  sportId,
  customColumns,
  customFilters,
  onEditCell,
  competitors = [],
  seasonId,
  genderType,
  refetchCompetitors,
}: UseControllerProps<T> & CompetitorsFormFieldProps) => {
  const { id } = useParams<string>();
  const idsRef = useRef<Record<number, HTMLDivElement>>({});
  const isSeason = !!useMatch(`${url.base_seasons}/:seasonId`);
  const isGroup = !!useMatch(`${url.base_groups}/:groupId`);
  const baseUrl = isSeason ? url.seasons : isGroup ? url.groups : url.rounds;
  const { setSortBy, setFilterBy, filteredItems } = useTableFilter<EditableCompetitor>(competitors, {
    columns: customColumns,
  });
  const rows = filteredItems.map(competitor => ({
    ...competitor,
    actions: {
      value: (
        <IconButton
          data-testid="add-relation-delete"
          title="delete"
          onClick={() => handleDelete(competitor)}
          size="large"
        >
          <Delete />
        </IconButton>
      ),
    },
  }));
  const handleAdd = useCallback(
    async (competitor: any) => {
      const result = await axios.post(`${baseUrl}/${id}${url.competitors}`, {
        competitorId: competitor.id,
      });
      if (result.status === 201) {
        refetchCompetitors();
      }
    },
    [baseUrl, id, refetchCompetitors],
  );

  const handleDelete = useCallback(
    async (competitor: EditableCompetitor) => {
      const result = await axios.delete(`${baseUrl}/${id}${url.competitors}/${competitor.id}`);
      if (result.status === 200) {
        refetchCompetitors();
      }
    },
    [baseUrl, id, refetchCompetitors],
  );

  const theme = useTheme();
  const stateColors = getCompetitorStateColors(theme);

  const handleBackgroundColorChange = useCallback(
    (competitorId: number, backgroundColor: string) => {
      const parentElement = idsRef.current[competitorId].parentElement;
      parentElement.parentElement.style.backgroundColor = backgroundColor;
      parentElement.style.backgroundColor = backgroundColor;
    },
    [idsRef],
  );

  useEffect(() => {
    rows.forEach(competitor => {
      if (competitor.deleted) {
        handleBackgroundColorChange(competitor.id, stateColors.deleted);
      } else if (competitor.edited) {
        handleBackgroundColorChange(competitor.id, stateColors.edited);
      } else if (competitor.added) {
        handleBackgroundColorChange(competitor.id, stateColors.added);
      } else {
        handleBackgroundColorChange(competitor.id, '');
      }
    });
  }, [handleBackgroundColorChange, rows, stateColors]);

  return (
    <BaseCompetitorsFormField
      sportId={sportId}
      seasonId={seasonId}
      customColumns={customColumns}
      customFilters={customFilters}
      onAdd={handleAdd}
      competitors={competitors}
      genderType={genderType}
      rows={rows}
      idsRef={idsRef}
      setFilterBy={setFilterBy}
      setSortBy={setSortBy}
      onEditCell={onEditCell}
    />
  );
};
